import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NikLogo } from "@/components/Logo";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const inputCls = "w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 pr-12 text-white outline-none focus:border-[var(--nik-blue)] transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reset failed. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4">
      <img
        src="https://images.unsplash.com/photo-1727018663219-b0bd25a359e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
        alt="" className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#0B0E14]/85 backdrop-blur-sm" />

      <div className="relative w-full max-w-md bg-[#121824]/90 border border-white/10 rounded-2xl p-8 fade-up">
        <div className="flex justify-center mb-6"><NikLogo size={40} /></div>
        <h1 className="font-display text-2xl font-bold text-center text-white">Set a new password</h1>
        <p className="text-center text-slate-400 text-sm mt-1">Choose a strong password for your KalamPrime account.</p>

        {!token ? (
          <div className="mt-6 text-center text-sm text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3">
            This reset link is invalid. Request a new one from the Sign In page.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <div className="relative">
              <input
                data-testid="reset-password-input" type={show ? "text" : "password"}
                value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="New password"
                className={inputCls}
              />
              <button type="button" data-testid="reset-show-password-btn" onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <input
              data-testid="reset-confirm-input" type="password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Confirm new password"
              className={inputCls}
            />
            <button
              data-testid="reset-submit-btn" type="submit" disabled={busy}
              className="w-full bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 nik-glow-btn disabled:opacity-60"
            >
              {busy && <Loader2 size={18} className="animate-spin" />} Update Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
